import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { useFieldHistoryReplay } from '@/hooks/useFieldHistoryReplay'
import { cn } from '@/lib/utils'
import { useFieldHistoryStore } from '@/store/field-history.store'
import { shouldIgnoreKeyboardEvent } from '@/utils/keyboard-utils'
import {
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react'

export function FieldHistoryControls({ className }: { className?: string }) {
  const canUndo = useFieldHistoryStore(state => state.canUndo())
  const canRedo = useFieldHistoryStore(state => state.canRedo())
  const isReplaying = useFieldHistoryStore(state => state.isReplaying)
  const { undo, redo } = useFieldHistoryReplay()

  if (!canUndo && !canRedo) {
    return null
  }

  return (
    <div
      className={cn(
        'flex items-center rounded-md border border-border/70 bg-background/80 shadow-sm',
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 rounded-r-none p-0"
        disabled={!canUndo || isReplaying}
        onClick={() => void undo()}
        title="Undo"
        aria-label="Undo"
      >
        <ArrowCounterClockwiseIcon className="h-4 w-4" />
      </Button>
      <div className="h-4 w-px bg-border/70" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 rounded-l-none p-0"
        disabled={!canRedo || isReplaying}
        onClick={() => void redo()}
        title="Redo"
        aria-label="Redo"
      >
        <ArrowClockwiseIcon className="h-4 w-4" />
      </Button>
    </div>
  )
}

export function FieldHistoryTextLinks({ className }: { className?: string }) {
  const canUndo = useFieldHistoryStore(state => state.canUndo())
  const canRedo = useFieldHistoryStore(state => state.canRedo())
  const isReplaying = useFieldHistoryStore(state => state.isReplaying)
  const { undo, redo } = useFieldHistoryReplay()

  if (!canUndo && !canRedo) {
    return null
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {canUndo && (
        <Button
          type="button"
          variant="link"
          disabled={isReplaying}
          onClick={() => void undo()}
          className="h-auto p-0 text-xs font-medium text-muted-foreground no-underline hover:text-foreground hover:no-underline"
        >
          Undo
        </Button>
      )}
      {canUndo && canRedo && <span className="text-xs text-border">/</span>}
      {canRedo && (
        <Button
          type="button"
          variant="link"
          disabled={isReplaying}
          onClick={() => void redo()}
          className="h-auto p-0 text-xs font-medium text-muted-foreground no-underline hover:text-foreground hover:no-underline"
        >
          Redo
        </Button>
      )}
    </div>
  )
}

export function FieldHistoryShortcuts() {
  const canUndo = useFieldHistoryStore(state => state.canUndo())
  const canRedo = useFieldHistoryStore(state => state.canRedo())
  const isReplaying = useFieldHistoryStore(state => state.isReplaying)
  const { undo, redo } = useFieldHistoryReplay()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreKeyboardEvent(event) || isReplaying) return

      const lowerKey = event.key.toLowerCase()
      const isModKey = event.metaKey || event.ctrlKey
      if (!isModKey) return

      if (lowerKey === 'z' && event.shiftKey) {
        if (!canRedo) return
        event.preventDefault()
        void redo()
        return
      }

      if (lowerKey === 'y') {
        if (!canRedo) return
        event.preventDefault()
        void redo()
        return
      }

      if (lowerKey === 'z') {
        if (!canUndo) return
        event.preventDefault()
        void undo()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [canRedo, canUndo, isReplaying, redo, undo])

  return null
}
